<template>
    <hts-drawer
        v-model="visible"
        color="main"
        size="medium"
        :rules="rules"
        :title="$t('labels.editNodeConnection')"
        :submit="submitEditNodeConnection"
        @close="close()">
        <hts-card
            v-loading="!visible"
            :title="$t('labels.generalInformation')">
            <div class="grid gap-4 grid-cols-1">
                <hts-form-input
                    v-model="startNode.name"
                    :label="$t('labels.startNode')"
                    prop="startNode"
                    required
                    disabled />

                <span>
                    {{ $t('labels.businessConnection') }}
                    <span class="text-error-500 ml-1">
                        *
                    </span>
                </span>
                <div
                    v-for="(business, index) in businessData"
                    :key="index">
                    <hts-form-disclosure
                        v-if="Object.keys(startNode.businesses).includes(business._id)"
                        color="main"
                        size="large"
                        :is-have-children="true">
                        <template #reference>
                            <div class="flex align-middle items-center gap-3 min-h-10 rounded-lg text-white bg-main-500 hover:bg-main-600 w-full">
                                <input
                                    class="w-[17.5px] h-[17.5px]"
                                    type="checkbox"
                                    :checked="Object.keys(dataEdit.businesses)?.includes(business._id)"
                                    :value="business._id"
                                    @click="(e) => businessClick(e, business._id)" >
                                <span>
                                    {{ business.name }}
                                </span>
                            </div>
                        </template>
                        <template #children>
                            <div
                                v-for="(task, subIndex) in business.tasks"
                                :key="task">
                                <div class="flex align-middle items-center gap-3 min-h-10 rounded-lg text-white mt-3 ml-10 p-2 pl-3 bg-main-400">
                                    <input
                                        class="w-[17.5px] h-[17.5px]"
                                        type="checkbox"
                                        disabled
                                        :checked="Object.keys(startNode.businesses).includes(business._id) && startNode.businesses[business._id]?.includes(task._id)"
                                        :value="task._id" >
                                    <span>
                                        {{ task.name }}
                                    </span>
                                </div>
                            </div>
                        </template>
                    </hts-form-disclosure>
                </div>

                <hts-form-select
                    v-model="dataEdit.nodeConnection"
                    :text="nodeConnectionSelected"
                    :query="findNodeConnection"
                    :filterable="true"
                    :clearable="true"
                    :label="$t('labels.nodeConnection')"
                    required
                    prop="nodeConnection"
                    key-id="_id"
                    key-label="name">
                    <template #custom>
                        <hts-tooltip class="mt-2 ml-2">
                            <div>
                                <span class="font-bold">
                                    {{ $t('labels.noteNodeConnection') }}
                                </span>
                            </div>
                            <template #reference>
                                <hi-information-circle-icon class="flex items-center text-green-500 w-6 h-6" />
                            </template>
                        </hts-tooltip>
                    </template>
                </hts-form-select>
            </div>
        </hts-card>
    </hts-drawer>
</template>

<script setup lang="ts">
const emits = defineEmits(['refreshTables'])
const { $disableReactive } = useNuxtApp()

const visible = ref(false)
const nodeId = ref('')
const connectionId = ref('')
const changeNodeConnection = ref(false)

const startNode = ref({
  name: '',
  nodeType: '',
  businesses: {} as { [key: string]: string[] }
})
const dataEdit = ref({
  startNode: '',
  businesses: {} as { [key: string]: string[] },
  nodeConnection: ''
})
const nodeConnectionSelected = ref<String>('')
const businessData = ref<{ [key: string]: string }[]>([])
const rules = {
  startNode: 'required'
  // nodeConnection: 'required'
}

const open = async (id: string, idC: string) => {
  nodeId.value = id
  connectionId.value = idC
  dataEdit.value.startNode = id
  await getData()
  await getDataConnection()
  visible.value = true
}

const close = () => {
  changeNodeConnection.value = false
  visible.value = false
}

const resetField = () => {
  nodeId.value = ''
  connectionId.value = ''
  dataEdit.value.startNode = ''
  startNode.value.name = ''
  startNode.value.nodeType = ''
  startNode.value.businesses = {}
  dataEdit.value.businesses = {}
  dataEdit.value.nodeConnection = ''
}

const getData = async () => {
  if (!nodeId.value) {
    return
  }
  const api = $urlAdmin.node.getById
  const data: any = await $api(api, { uriParams: { ID: nodeId.value } })
  const resData: any = data?.data?.value
  if (resData?.success) {
    startNode.value.name = resData.data?.location?.locationName || ''
    startNode.value.nodeType = resData.data?.nodeType?._id || ''
    startNode.value.businesses = $disableReactive(resData.data?.businesses) || {}
  }
}

const getDataConnection = async () => {
  if (!connectionId.value) {
    return
  }
  const api = $urlAdmin.node.connection.getById
  const data: any = await $api(api, { uriParams: { ID: connectionId.value } })
  const resData: any = data?.data?.value
  if (resData?.success) {
    dataEdit.value.businesses = resData.data.businesses
    if (resData.data?.nodeConnection) {
      const nodeConnection: any = resData.data?.nodeConnection
      nodeConnectionSelected.value = nodeConnection?.nodeType?.name + ' - ' + nodeConnection?.location?.locationName
      dataEdit.value.nodeConnection = nodeConnection._id
    }
  }
}

watch(
  () => startNode.value.nodeType,
  async (val) => {
    // lấy thông tin nghiệp vụ
    const api = $urlAdmin.business.getByNodeType
    if (!val) {
      return
    }

    const data: any = await $api(api, { uriParams: { ID: val } })
    const resData = data?.data?.value
    if (resData?.success) {
      // data update UI
      businessData.value = resData.data
    }
  }
)

const businessClick = (e: any, id: string) => {
  e.stopPropagation()
  if (!Object.keys(dataEdit.value.businesses)?.includes(id)) {
    dataEdit.value.businesses[id] = [...startNode.value.businesses[id]]
  } else {
    delete dataEdit.value.businesses[id]
  }
}

watch(
  () => dataEdit.value.businesses,
  (val) => {
    if (!changeNodeConnection.value) {
      changeNodeConnection.value = true
      return
    }
    dataEdit.value.nodeConnection = ''
  },
  { deep: true }
)

const findNodeConnection = async (s: string) => {
  const api = $urlAdmin.node.findNodeConnections

  const res = await $api(api, {
    uriParams: {
      KEY: s,
      LIMIT: 100
    },
    body: dataEdit.value
  })
  const resData: any = res?.data?.value
  if (resData?.success) {
    return resData.data
  }
  return null
}

const submitEditNodeConnection = () => {
  if (dataEdit.value?.businesses && Object.keys(dataEdit.value?.businesses)?.length === 0) {
    process.client && $toast().error($t('labels.pleaseSelectBusinessConnection'))
    return
  }

  if (!connectionId.value) {
    return
  }

  const api = $urlAdmin.node.connection.edit
  $api(api, {
    body: dataEdit.value,
    uriParams: { ID: connectionId.value }
  }).then((data) => {
    const resData: any = data?.data?.value

    if (resData?.success) {
      process.client && $toast().success($t('labels.editSuccess'))
      resetField()
      close()
      emits('refreshTables')
    }
  })
}

defineExpose({
  open
})
</script>
